import type { EditorState, TattooLayer } from './types';

const stateKey = (state: EditorState): string => JSON.stringify(state);

/**
 * History snapshots copy mutable layer records while sharing completed mask
 * strokes. A stroke becomes immutable as soon as its gesture commits, so its
 * point buffer is stored once even when many later logical states reference it.
 */
export function cloneHistoryState(state: EditorState): EditorState {
  return {
    photo: { ...state.photo },
    selectedLayerId: state.selectedLayerId,
    layers: state.layers.map((layer): TattooLayer => ({
      ...layer,
      maskStrokes: layer.maskStrokes.slice(),
    })),
  };
}

export class EditorHistory {
  private entries: EditorState[] = [];
  private cursor = -1;
  private transactionStart: EditorState | null = null;

  constructor(readonly safetyLimit = 2000) {}

  reset(initial: EditorState): void {
    this.entries = [cloneHistoryState(initial)];
    this.cursor = 0;
    this.transactionStart = null;
  }

  begin(state: EditorState): void {
    if (!this.transactionStart) this.transactionStart = cloneHistoryState(state);
  }

  commit(state: EditorState): boolean {
    const start = this.transactionStart;
    this.transactionStart = null;
    if (start && stateKey(start) === stateKey(state)) return false;
    return this.record(state);
  }

  cancel(): void { this.transactionStart = null; }

  record(state: EditorState, force = false): boolean {
    this.transactionStart = null;
    const current = this.entries[this.cursor];
    if (!force && current && stateKey(current) === stateKey(state)) return false;
    this.entries = this.entries.slice(0, this.cursor + 1);
    this.entries.push(cloneHistoryState(state));
    if (this.entries.length > this.safetyLimit) this.entries.splice(0, this.entries.length - this.safetyLimit);
    this.cursor = this.entries.length - 1;
    return true;
  }

  undo(): EditorState | null {
    if (!this.canUndo) return null;
    this.cursor -= 1;
    this.transactionStart = null;
    return cloneHistoryState(this.entries[this.cursor]);
  }

  redo(): EditorState | null {
    if (!this.canRedo) return null;
    this.cursor += 1;
    this.transactionStart = null;
    return cloneHistoryState(this.entries[this.cursor]);
  }

  get canUndo(): boolean { return this.cursor > 0; }
  get canRedo(): boolean { return this.cursor >= 0 && this.cursor < this.entries.length - 1; }
  get length(): number { return this.entries.length; }
  get index(): number { return this.cursor; }
  get transactionActive(): boolean { return Boolean(this.transactionStart); }

  estimatedBytes(): number {
    return this.entries.reduce((total, entry) => total + stateKey(entry).length * 2, 0);
  }
}
